import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ClientDetails from "@/pages/ClientDetails";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Pencil, Trash2, Eye, RefreshCcw, Search, FileText, Upload, X, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTenant } from "@/contexts/TenantContext";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { ClientFilters, ClientFilterState } from "@/components/clients/ClientFilters";
import { getGravatarUrl } from "@/lib/gravatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatPhone } from "@/lib/utils";
import { format } from "date-fns";
import { logCrud } from "@/services/activities";

type Client = {
  id: string;
  nome: string;
  cpf_cnpj: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  endereco_completo: string | null;
  created_at?: string | null;
  data_nascimento: string | null;
  observacoes: string | null;
  chatwoot_contact_id: number | null;
  foto_url: string | null;
  chatwoot_avatar_url: string | null;
};

const extractDigits = (value: string) => value.replace(/\D/g, "");

const normalizePhoneDigits = (value: string) => {
  if (!value) return "";
  let digits = extractDigits(value);
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length > 11) {
    digits = digits.slice(2);
  }
  return digits.slice(0, 11);
};

const formatPhoneBR = (value: string | null | undefined) => {
  const digits = normalizePhoneDigits(value || "");
  if (!digits) return "";

  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{0,2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 14);
  }

  return digits
    .replace(/^(\d{0,2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .slice(0, 15);
};

const formatCpfCnpj = (value: string | null | undefined) => {
  const digits = extractDigits(value || "");
  if (!digits) return "";

  // Se tiver 11 dígitos ou menos, aplicar máscara de CPF
  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{0,3})(\d)/, "$1.$2")
      .replace(/^(\d{3}\.\d{3}\.\d{0,3})(\d{0,2})$/, "$1-$2")
      .slice(0, 14);
  }

  // Se tiver mais de 11 dígitos, aplicar máscara de CNPJ (até 14 dígitos)
  const cnpjDigits = digits.slice(0, 14);
  return cnpjDigits
    .replace(/^(\d{0,2})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{0,3})(\d)/, "$1.$2")
    .replace(/^(\d{2}\.\d{3}\.\d{0,3})(\d)/, "$1/$2")
    .replace(/^(\d{2}\.\d{3}\.\d{3}\/\d{0,4})(\d{0,2})$/, "$1-$2")
    .slice(0, 18);
};

const sanitizeDigits = (value: FormDataEntryValue | null) => {
  if (!value) return null;
  const digits = extractDigits(String(value));
  return digits ? digits : null;
};

const sanitizePhone = (value: FormDataEntryValue | null) => {
  const digits = normalizePhoneDigits(String(value ?? ""));
  return digits ? digits : null;
};

// Helper para obter foto do cliente (foto_url -> chatwoot_avatar_url -> gravatar)
const getClientPhoto = (client: Client): string | null => {
  if (client.foto_url) return client.foto_url;
  if (client.chatwoot_avatar_url) return client.chatwoot_avatar_url;
  return getGravatarUrl(client.email);
};

export default function Clients() {
  const [open, setOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [syncingClientId, setSyncingClientId] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [shouldRemovePhoto, setShouldRemovePhoto] = useState(false);
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { hasToken, markTokenInvalid } = useChatwootToken();
  const [clientProfile, setClientProfile] = useState<{ id: string; name: string } | null>(null);
  const [filters, setFilters] = useState<ClientFilterState>({
    searchTerm: "",
    hasChatwoot: null,
    ageRange: "",
  });

  const uploadPhoto = async (file: File, clientId: string) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${clientId}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('client-photos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('client-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const deletePhoto = async (url: string) => {
    if (!url) return;
    try {
      const path = url.split('/client-photos/')[1];
      if (path) {
        await supabase.storage.from('client-photos').remove([path]);
      }
    } catch (error) {
      console.error("Erro ao deletar foto:", error);
    }
  };

  const { data: clients, isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("nome", { ascending: true });
      
      if (error) throw error;
      return data as Client[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!tenantId) throw new Error("Tenant ID não encontrado");

      const insertPayload = {
        nome: formData.get("nome") as string,
        cpf_cnpj: sanitizeDigits(formData.get("cpf_cnpj")),
        telefone: sanitizePhone(formData.get("telefone")),
        whatsapp: sanitizePhone(formData.get("whatsapp")),
        email: (formData.get("email") as string) || null,
        endereco_completo: (formData.get("endereco_completo") as string) || null,
        data_nascimento: (formData.get("data_nascimento") as string) || null,
        observacoes: (formData.get("observacoes") as string) || null,
        tenant_id: tenantId,
        foto_url: null as string | null,
      };

      const { data, error } = await supabase
        .from("clients")
        .insert(insertPayload)
        .select("id")
        .single();
      if (error) throw error;

      const clientId = data?.id as string | undefined;
      let photoUrl: string | null = null;

      if (clientId && photoFile) {
        photoUrl = await uploadPhoto(photoFile, clientId);
        const { error: updateError } = await supabase
          .from("clients")
          .update({ foto_url: photoUrl })
          .eq("id", clientId);
        if (updateError) throw updateError;
      }

      if (clientId && hasToken) {
        try {
          const sdk = getChatwootSDK();
          await sdk.syncContact("client", clientId);
        } catch (syncError: any) {
          console.error("Erro ao sincronizar cliente com Chatwoot", syncError);
          handleChatwootFailure(
            syncError,
            "Cliente salvo, mas não foi possível sincronizar com o Chatwoot.",
            "warning",
            "Cliente salvo, mas o token do Chatwoot é inválido. Atualize o token em Configurações > Integrações > Chatwoot."
          );
        }
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente criado com sucesso");
      const name = variables.get("nome") as string;
      void logCrud({ entitySlug: "entity:clients", operationSlug: "create", entityName: name, screenSlug: "clientes" });
      setOpen(false);
      setPhotoFile(null);
      setPhotoPreview(null);
    },
    onError: () => toast.error("Erro ao criar cliente"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: FormData }) => {
      const updatePayload: Record<string, unknown> = {
        nome: formData.get("nome") as string,
        cpf_cnpj: sanitizeDigits(formData.get("cpf_cnpj")),
        telefone: sanitizePhone(formData.get("telefone")),
        whatsapp: sanitizePhone(formData.get("whatsapp")),
        email: (formData.get("email") as string) || null,
        endereco_completo: (formData.get("endereco_completo") as string) || null,
        data_nascimento: (formData.get("data_nascimento") as string) || null,
        observacoes: (formData.get("observacoes") as string) || null,
      };

      let photoUrl: string | null | undefined = editingClient?.foto_url;

      // Se o usuário clicou para remover a foto
      if (shouldRemovePhoto) {
        if (editingClient?.foto_url) {
          await deletePhoto(editingClient.foto_url);
        }
        photoUrl = null;
        updatePayload.foto_url = null;
      } else if (photoFile) {
        // Se está adicionando uma nova foto
        if (editingClient?.foto_url) {
          await deletePhoto(editingClient.foto_url);
        }
        photoUrl = await uploadPhoto(photoFile, id);
        updatePayload.foto_url = photoUrl;
      }

      const { error } = await supabase
        .from("clients")
        .update(updatePayload)
        .eq("id", id);
      if (error) throw error;

      if (hasToken) {
        try {
          const sdk = getChatwootSDK();
          await sdk.syncContact("client", id);
        } catch (syncError: any) {
          console.error("Erro ao sincronizar cliente com Chatwoot", syncError);
          handleChatwootFailure(
            syncError,
            "Cliente atualizado, mas não foi possível sincronizar com o Chatwoot.",
            "warning",
            "Cliente atualizado, mas o token do Chatwoot é inválido. Atualize o token em Configurações > Integrações > Chatwoot."
          );
        }
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente atualizado com sucesso");
      void logCrud({ entitySlug: "entity:clients", operationSlug: "update", entityId: (variables as any).id, screenSlug: "clientes", before: (editingClient as any), after: (variables as any) });
      setOpen(false);
      setEditingClient(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setShouldRemovePhoto(false);
    },
    onError: () => toast.error("Erro ao atualizar cliente"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast.success("Cliente excluído com sucesso");
      if (deleteId) {
        void logCrud({ entitySlug: "entity:clients", operationSlug: "delete", entityId: deleteId, screenSlug: "clientes" });
      }
      setDeleteId(null);
    },
    onError: (error: unknown) => {
      const err = error as { code?: string; message?: string };
      const isFkConflict =
        err?.code === "23503" ||
        err?.message?.toLowerCase?.().includes("foreign key") ||
        err?.message?.toLowerCase?.().includes("conflict");

      if (isFkConflict) {
        toast.error(
          "Não foi possível excluir: há registros vinculados a este cliente (ex.: vendas). Considere inativar o cliente em vez de excluir."
        );
      } else {
        toast.error("Erro ao excluir cliente");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    if (editingClient) {
      updateMutation.mutate({ id: editingClient.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleOpenDialog = (client?: Client) => {
    setEditingClient(client || null);
    setPhotoFile(null);
    setShouldRemovePhoto(false);
    // Mostrar preview apenas se houver foto real (upload ou Chatwoot), não Gravatar
    if (client) {
      const realPhoto = client.foto_url || client.chatwoot_avatar_url || null;
      setPhotoPreview(realPhoto);
    } else {
      setPhotoPreview(null);
    }
    setOpen(true);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setShouldRemovePhoto(false); // Se está adicionando nova foto, não remover
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setShouldRemovePhoto(true); // Marcar que a foto deve ser removida
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleChatwootFailure = (
    error: unknown,
    fallbackMessage: string,
    variant: "error" | "warning" = "error",
    tokenInvalidMessage?: string
  ) => {
    const message = typeof (error as any)?.message === "string" ? (error as any).message : "";
    if (message.includes("401")) {
      markTokenInvalid();
      toast.error(
        tokenInvalidMessage ||
          `${fallbackMessage} Token do Chatwoot inválido. Atualize o token em Configurações > Integrações > Chatwoot.`
      );
    } else {
      if (variant === "warning") {
        toast.warning(message || fallbackMessage);
      } else {
        toast.error(message || fallbackMessage);
      }
    }
  };

  const calculateAge = (birthDate: string | null): number | null => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const matchesFilters = (client: Client) => {
    // Filtro por busca
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        client.nome?.toLowerCase().includes(searchLower) ||
        client.cpf_cnpj?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.telefone?.toLowerCase().includes(searchLower) ||
        client.whatsapp?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filtro por faixa etária
    if (filters.ageRange) {
      if (filters.ageRange === "sem-data") {
        if (client.data_nascimento) return false;
      } else {
        if (!client.data_nascimento) return false;
        const age = calculateAge(client.data_nascimento);
        if (age === null) return false;
        
        const [min, max] = filters.ageRange.includes("+")
          ? [70, Infinity]
          : filters.ageRange.split("-").map(Number);
        if (age < min || age > max) return false;
      }
    }

    // Filtro por Chatwoot (apenas se houver integração ativa)
    if (hasToken && filters.hasChatwoot !== null) {
      const hasChatwoot = Boolean(client.chatwoot_contact_id);
      if (filters.hasChatwoot !== hasChatwoot) return false;
    }

    return true;
  };

  const filteredClients = useMemo(() => {
    if (!clients) return [] as Client[];
    return clients.filter((client) => matchesFilters(client));
  }, [clients, filters, hasToken]);

  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredClients.slice(start, start + itemsPerPage);
  }, [filteredClients, currentPage, itemsPerPage]);

  // Calcular contagens para os filtros
  const facetCounts = useMemo(() => {
    const counts = {
      hasChatwoot: {
        vinculado: 0,
        naoVinculado: 0,
      },
      ageRanges: {} as Record<string, number>,
    };

    if (!clients) return counts;

    clients.forEach((client) => {
      // Verificar se o cliente corresponde aos outros filtros (exceto o que está sendo contado)
      const matchesOtherFilters = (exceptFilter?: "hasChatwoot" | "ageRange") => {
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            client.nome?.toLowerCase().includes(searchLower) ||
            client.cpf_cnpj?.toLowerCase().includes(searchLower) ||
            client.email?.toLowerCase().includes(searchLower) ||
            client.telefone?.toLowerCase().includes(searchLower) ||
            client.whatsapp?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        if (exceptFilter !== "ageRange" && filters.ageRange) {
          if (filters.ageRange === "sem-data") {
            if (client.data_nascimento) return false;
          } else {
            if (!client.data_nascimento) return false;
            const age = calculateAge(client.data_nascimento);
            if (age === null) return false;
            
            const [min, max] = filters.ageRange.includes("+")
              ? [70, Infinity]
              : filters.ageRange.split("-").map(Number);
            if (age < min || age > max) return false;
          }
        }

        if (exceptFilter !== "hasChatwoot" && filters.hasChatwoot !== null) {
          const hasChatwoot = Boolean(client.chatwoot_contact_id);
          if (filters.hasChatwoot !== hasChatwoot) return false;
        }

        return true;
      };

      // Contar faixa etária (ignorando filtro de idade)
      if (matchesOtherFilters("ageRange")) {
        if (!client.data_nascimento) {
          counts.ageRanges["sem-data"] = (counts.ageRanges["sem-data"] || 0) + 1;
        } else {
          const age = calculateAge(client.data_nascimento);
          if (age !== null) {
            const bucket =
              age <= 18 ? "0-18" :
              age <= 30 ? "19-30" :
              age <= 50 ? "31-50" :
              age <= 70 ? "51-70" :
              "70+";
            counts.ageRanges[bucket] = (counts.ageRanges[bucket] || 0) + 1;
          }
        }
      }

      // Contar Chatwoot (ignorando filtro de Chatwoot) - apenas se houver integração ativa
      if (hasToken && matchesOtherFilters("hasChatwoot")) {
        if (client.chatwoot_contact_id) {
          counts.hasChatwoot.vinculado++;
        } else {
          counts.hasChatwoot.naoVinculado++;
        }
      }
    });

    return counts;
  }, [clients, filters, hasToken]);

  const handleFiltersChange = (newFilters: ClientFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    if (filters.ageRange) {
      let labelRange = "";
      if (filters.ageRange === "0-18") labelRange = "0-18 anos";
      else if (filters.ageRange === "19-30") labelRange = "19-30 anos";
      else if (filters.ageRange === "31-50") labelRange = "31-50 anos";
      else if (filters.ageRange === "51-70") labelRange = "51-70 anos";
      else if (filters.ageRange === "70+") labelRange = "Acima de 70 anos";
      else if (filters.ageRange === "sem-data") labelRange = "Sem data de nascimento";
      
      chips.push({
        id: `age:${filters.ageRange}`,
        label: `Idade: ${labelRange}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, ageRange: "" });
        },
      });
    }
    
    if (hasToken && filters.hasChatwoot !== null) {
      chips.push({
        id: `chatwoot:${filters.hasChatwoot}`,
        label: `Chatwoot: ${filters.hasChatwoot ? "Vinculado" : "Não vinculado"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasChatwoot: null });
        },
      });
    }
    
    return chips;
  }, [filters]);

  const renderChatwootStatus = (client: Client) => {
    if (!hasToken) return null;

    if (client.chatwoot_contact_id) {
      return (
        <span className="inline-flex items-center gap-1 text-xs rounded bg-emerald-100 text-emerald-700 px-2 py-1">
          Vinculado
        </span>
      );
    }

    const hasSyncData = Boolean(client.telefone || client.whatsapp || client.email);

    if (!hasSyncData) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs rounded bg-muted text-muted-foreground px-2 py-1 cursor-not-allowed"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.message("Para sincronizar este contato, adicione telefone e/ou email.");
                }}
              >
                <RefreshCcw className="h-3 w-3" />
                Pendente
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Para sincronizar este contato, adicione telefone e/ou email.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs rounded bg-amber-100 text-amber-700 px-2 py-1 hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
        disabled={syncingClientId === client.id}
        onClick={async (e) => {
          e.stopPropagation();
          if (syncingClientId) return;
          try {
            setSyncingClientId(client.id);
            const sdk = getChatwootSDK();
            await sdk.syncContact("client", client.id);
            toast.success("Sincronização com Chatwoot concluída.");
            queryClient.invalidateQueries({ queryKey: ["clients"] });
          } catch (error: any) {
            handleChatwootFailure(error, "Falha ao sincronizar com o Chatwoot.");
          } finally {
            setSyncingClientId(null);
          }
        }}
      >
        <RefreshCcw className={`h-3 w-3 ${syncingClientId === client.id ? "animate-spin" : ""}`} />
        Pendente
      </button>
    );
  };

  return (
    <div className="space-y-6" data-cy="clients-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-cy="clients-title">Clientes</h1>
          <p className="text-muted-foreground">Gerencie seus clientes</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto" onClick={() => handleOpenDialog()} data-cy="new-client-btn">
              <Plus className="mr-2 h-4 w-4" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl p-0 overflow-hidden" data-cy="client-dialog">
            <div className="flex max-h-[90vh] flex-col">
              <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
                <DialogTitle>
                  {editingClient ? "Editar Cliente" : "Novo Cliente"}
                </DialogTitle>
                <DialogDescription>
                  Preencha os dados do cliente
                </DialogDescription>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto px-6 py-4">
                <form id="client-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Foto do Cliente e Campos Principais */}
              <div className="flex gap-6 items-start">
                {/* Foto do Cliente */}
                <div className="flex-shrink-0">
                  <Label className="mb-2 block">Foto do Cliente</Label>
                  <Input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  {photoPreview ? (
                    <div className="relative">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-32 h-32 rounded-full object-cover border-2 border-border cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => document.getElementById('photo')?.click()}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto();
                        }}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90 z-10"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="w-32 h-32 rounded-full bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => document.getElementById('photo')?.click()}
                    >
                      <User className="h-16 w-16 text-muted-foreground" />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Clique para adicionar
                  </p>
                </div>

                {/* Campos ao lado da foto */}
                <div className="flex-1 space-y-4">
                  {/* Primeira linha: Nome e E-mail */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome Completo/Razão Social *</Label>
                      <Input
                        id="nome"
                        name="nome"
                        defaultValue={editingClient?.nome}
                        required
                        data-cy="client-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        defaultValue={editingClient?.email || ""}
                        data-cy="client-email-input"
                      />
                    </div>
                  </div>

                  {/* Segunda linha: CPF/CNPJ, Data de Nascimento, Telefone e WhatsApp */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
                      <Input
                        id="cpf_cnpj"
                        name="cpf_cnpj"
                        defaultValue={formatCpfCnpj(editingClient?.cpf_cnpj || "")}
                        inputMode="numeric"
                        maxLength={18}
                        onKeyDown={(event) => {
                          // Permitir apenas números e teclas de controle
                          const key = event.key;
                          const isNumber = /^[0-9]$/.test(key);
                          const isControlKey = [
                            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
                            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                            'Home', 'End'
                          ].includes(key);
                          const isCtrlA = event.ctrlKey && key === 'a';
                          const isCtrlC = event.ctrlKey && key === 'c';
                          const isCtrlV = event.ctrlKey && key === 'v';
                          const isCtrlX = event.ctrlKey && key === 'x';
                          
                          if (!isNumber && !isControlKey && !isCtrlA && !isCtrlC && !isCtrlV && !isCtrlX) {
                            event.preventDefault();
                          }
                        }}
                        onInput={(event) => {
                          const input = event.currentTarget;
                          const cursorPosition = input.selectionStart || 0;
                          const oldValue = input.value;
                          
                          // Aplicar a formatação
                          const newValue = formatCpfCnpj(input.value);
                          input.value = newValue;
                          
                          // Ajustar a posição do cursor após a formatação
                          const digitsBefore = extractDigits(oldValue.slice(0, cursorPosition)).length;
                          let newCursorPosition = 0;
                          let digitCount = 0;
                          
                          for (let i = 0; i < newValue.length && digitCount < digitsBefore; i++) {
                            if (/\d/.test(newValue[i])) {
                              digitCount++;
                            }
                            newCursorPosition = i + 1;
                          }
                          
                          // Se estava no final, manter no final
                          if (cursorPosition >= oldValue.length) {
                            newCursorPosition = newValue.length;
                          }
                          
                          // Usar setTimeout para garantir que o cursor seja posicionado após a atualização do DOM
                          setTimeout(() => {
                            input.setSelectionRange(newCursorPosition, newCursorPosition);
                          }, 0);
                        }}
                        data-cy="client-cpf-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                      <Input
                        id="data_nascimento"
                        name="data_nascimento"
                        type="date"
                        defaultValue={editingClient?.data_nascimento || ""}
                        data-cy="client-birthdate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        name="telefone"
                        type="tel"
                        defaultValue={formatPhoneBR(editingClient?.telefone || "")}
                        maxLength={15}
                        inputMode="tel"
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                        data-cy="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        type="tel"
                        defaultValue={formatPhoneBR(editingClient?.whatsapp || "")}
                        maxLength={15}
                        inputMode="tel"
                        onChange={(event) => {
                          event.currentTarget.value = formatPhoneBR(event.currentTarget.value);
                        }}
                        data-cy="client-whatsapp-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="endereco_completo">Endereço Completo</Label>
                  <Textarea
                    id="endereco_completo"
                    name="endereco_completo"
                    defaultValue={editingClient?.endereco_completo || ""}
                    rows={2}
                    data-cy="client-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    name="observacoes"
                    defaultValue={editingClient?.observacoes || ""}
                    rows={2}
                    data-cy="client-notes-input"
                  />
                </div>
              </div>

              </form>
              </div>
              <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="client-form"
                  data-cy="client-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingClient
                    ? isSubmitting
                      ? "Salvando..."
                      : "Salvar Cliente"
                    : isSubmitting
                      ? "Criando..."
                      : "Criar Cliente"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card data-cy="clients-table-card">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle data-cy="clients-table-title">Lista de Clientes</CardTitle>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="clients-search-input"
                  />
                </div>
                <ClientFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  hasChatwootIntegration={hasToken}
                  counts={facetCounts}
                  data-cy="clients-filters"
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8" data-cy="clients-loading">Carregando...</div>
          ) : filteredClients && filteredClients.length > 0 ? (
            <>
              <div className="space-y-3 md:hidden">
                {paginatedClients.map((client) => (
                  <div
                    key={client.id}
                    className="rounded-lg border bg-card shadow-sm transition hover:shadow-md"
                    onClick={() => handleOpenDialog(client)}
                    data-cy={`client-card-${client.id}`}
                  >
                    <div className="flex flex-col gap-3 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                          {getClientPhoto(client) && (
                            <AvatarImage src={getClientPhoto(client)!} alt={client.nome} />
                          )}
                          <AvatarFallback>
                            {client.nome
                              .split(" ")
                              .map((n) => n[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-1">
                          <div className="text-sm text-muted-foreground">Cliente</div>
                          <h3 className="text-base font-semibold leading-tight" data-cy={`client-name-${client.id}`}>
                            {client.nome}
                          </h3>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm text-muted-foreground">
                        {client.cpf_cnpj && (
                          <div>
                            <span className="font-medium text-foreground">CPF/CNPJ:</span> {formatCpfCnpj(client.cpf_cnpj)}
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-foreground">Telefone:</span> {formatPhoneBR(client.telefone || client.whatsapp || "") || "-"}
                        </div>
                        {client.email && (
                          <div>
                            <span className="font-medium text-foreground">E-mail:</span> {client.email}
                          </div>
                        )}
                      </div>
                      {hasToken && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Chatwoot:</span>
                          {renderChatwootStatus(client)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 border-t px-4 py-3 sm:flex-row">
                      <div className="flex flex-row gap-2 w-full">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setClientProfile({ id: client.id, name: client.nome });
                                }}
                                data-cy={`client-view-btn-mobile-${client.id}`}
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                Detalhes
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-center">
                              <div>
                                <p>Ver ficha completa e relatórios do cliente</p>
                                <p className="font-bold mt-1">{client.nome}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(client);
                                }}
                                data-cy={`client-edit-btn-mobile-${client.id}`}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-center">
                              <div>
                                <p>Editar dados do cliente</p>
                                <p className="font-bold mt-1">{client.nome}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(client.id);
                                }}
                                data-cy={`client-delete-btn-mobile-${client.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-center">
                              <div>
                                <p>Excluir cliente</p>
                                <p className="font-bold mt-1">{client.nome}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block">
                <Table data-cy="clients-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome / CPF / CNPJ</TableHead>
                      <TableHead>Telefone / Email</TableHead>
                      {hasToken && <TableHead>Chatwoot</TableHead>}
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedClients.map((client) => (
                      <TableRow 
                        key={client.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleOpenDialog(client)}
                        data-cy={`client-row-${client.id}`}
                      >
                        <TableCell className="font-medium" data-cy={`client-name-${client.id}`}>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              {getClientPhoto(client) && (
                                <AvatarImage src={getClientPhoto(client)!} alt={client.nome} />
                              )}
                              <AvatarFallback>
                                {client.nome
                                  .split(" ")
                                  .map((n) => n[0])
                                  .slice(0, 2)
                                  .join("")
                                  .toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span>{client.nome}</span>
                              {client.cpf_cnpj && (
                                <span className="text-sm text-muted-foreground mt-1">
                                  {formatCpfCnpj(client.cpf_cnpj)}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell data-cy={`client-phone-${client.id}`}>
                          <div className="flex flex-col">
                            <span>
                              {formatPhoneBR(client.telefone || client.whatsapp || "") || "-"}
                            </span>
                            {client.email && (
                              <span className="text-sm text-muted-foreground mt-1">
                                {client.email}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        {hasToken && <TableCell>{renderChatwootStatus(client)}</TableCell>}
                        <TableCell className="text-center">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setClientProfile({ id: client.id, name: client.nome });
                                  }}
                                  data-cy={`client-view-btn-${client.id}`}
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                <div>
                                  <p>Ver ficha completa e relatórios do cliente</p>
                                  <p className="font-bold mt-1">{client.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-green"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDialog(client);
                                  }}
                                  data-cy={`client-edit-btn-${client.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                <div>
                                  <p>Editar dados do cliente</p>
                                  <p className="font-bold mt-1">{client.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="icon-chip-red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteId(client.id);
                                  }}
                                  data-cy={`client-delete-btn-${client.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                <div>
                                  <p>Excluir cliente</p>
                                  <p className="font-bold mt-1">{client.nome}</p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredClients.length / itemsPerPage)}
                totalItems={filteredClients.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
                data-cy="clients-pagination"
              />
            </>
          ) : clients && clients.length > 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-cy="clients-empty-filtered">
              Nenhum cliente encontrado com os filtros aplicados.
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground" data-cy="clients-empty">
              Nenhum cliente cadastrado. Clique em "Novo Cliente" para começar.
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {clientProfile && (() => {
        const c = (clients || []).find((x: any) => x.id === clientProfile.id);
        const photo = c?.foto_url || c?.chatwoot_avatar_url || (c?.email ? getGravatarUrl(c.email) : null);
        const infoItems = [
          c?.created_at ? { value: `Cliente desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(c.created_at))}` } : null,
          c?.telefone ? { value: formatPhone(c.telefone) } : null,
          c?.email ? { value: c.email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!clientProfile}
            onOpenChange={() => setClientProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"CLIENTE"}
            entityName={clientProfile.name}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <ClientDetails id={clientProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
    </div>
  );
}
